import { useCallback, useEffect, useMemo } from "react";
import _, { inRange } from "lodash";
import classnames from "classnames";
import { useRecoilState, useRecoilValue } from "recoil";

import mainStore, {
  popupsSelector,
  storeSetBookBetLoading,
  storeSetEachWay,
  storeSetIsProfitBoost, storeSetProfitBoostPercent,
  storeSetSuperbet, storeTriggerdBetId,
} from "src/stores/main";
import SuperbetToggle from "src/components/betBlock/components/superbetToggle";
import EachWayToggle from "src/components/betBlock/components/eachWayToggle";
import VerificationToggle from "src/components/betBlock/components/verificationToggle";
import { emitBetBooked, emitBetProcessing, emitProfitBoostInfo } from "src/services/communicator/actions";
import VerificationAccount from "src/components/betBlock/components/verificationAccount";
import SystemStakeInput from "./components/systemStakeInput";
import NotifyMessages from "src/components/notifyMessages";
import { BetTypeEnum } from "src/constants/enums/betType";
import { openDeferPopup } from "src/common/helpers/view";
import selectionsStore from "src/stores/selections";
import betBuilderStore from "src/stores/betBuilder";
import { decimalString } from "src/constants/types";
import OddConverter from "@common/helpers/oddConverter";
import upperDataStore from "src/stores/upperData";
import { isPlaceBetAvailable, placeBetDisabled } from "src/rules/bet";
import { useConfig } from "src/hooks/config";
import { placeBet } from "src/services/placeBet";
import { bookBet } from "src/services/bookBet";
import { useTranslation } from "src/hooks/translation";
import { formatBookingTicketSelections } from "src/common/helpers/bookingBet";
import { getEffectiveSelectionsCount } from "src/common/helpers/betBuilder";

import { BOOSTED_ODD_POPUP, CLEAR_BETSLIP_POPUP, LEAVE_EDITING_POPUP, SUCCESS_POPUP } from "../popups/constants";
import { BetCalculationsEnum, getBetCalculations } from "../../../calculation";
import BetDetails from "./components/betDetails";
import SystemOptions from "./components/systemOptions";
import PriceChangeTypeSelect from "../priceChangeTypeSelect";
import StakeInput from "../../common/components/stakeInput";
import PlaceBetButton from "./components/placeBetButton";
import SuperbetInfo from "./components/superbetInfo";
import { racingTypesList } from "src/constants/racingTypesList";
import BetDetailsTotalStake from "./components/totalStake";
import Spinner from "src/common/components/spinner";
import { roundAmount } from "src/rules/calculation/helpers";
import { toastWrapper } from "src/common/components/toastWrapper";
import ProfitBoost from "src/components/profitBoost";
import FreeBetToggle from "./components/freeBetToggle";
import FreeBets from "./components/freeBets";
import LeftSnow from "src/assets/images/left-snow.svg";
import { isBookingAvailable, isBookingOnlyMode } from "src/rules/booking";
import { invalidCounterOfferOdd } from "src/rules/counterOffer";

import "./style.scss";
import TerminalSwarmActions from "src/services/swarmActions/terminal";

interface IProps {
  onClear(removingSelectionIds?: number[], saveAsLastSelections?: boolean): void;
}

const BetBlock = ({ onClear }: IProps) => {
  const config = useConfig();
  const t = useTranslation();

  const upperDataState = useRecoilValue(upperDataStore);
  const [mainState, setMainState] = useRecoilState(mainStore);
  const [, setOpenPopup] = useRecoilState(popupsSelector);
  const { stake, betType, isSuperbet, maxStake, systemCombinationCount, isFreeBetEnabled, systemUnitStake } = mainState;
  const [{ selections }, setSelections] = useRecoilState(selectionsStore);
  const bbState = useRecoilValue(betBuilderStore);
  const { oddsFormat, partnerConfig, bonusRules, verificationResponse, currencyConfig } = upperDataState;
  const { profitBoostPercent, profitBoostStake, isProfitBoost } = useRecoilValue(mainStore);

  const handleClear = async () => {
    const isConfirmed = await openDeferPopup<boolean>(CLEAR_BETSLIP_POPUP);
    if (isConfirmed) {
      changeStake("");
      onClear();
    }
  };
  const { usageRules } = config.wheelConfig || {}

  const { totalOdd } = useMemo(
    () =>
      getBetCalculations([BetCalculationsEnum.TotalOdd], {
        config,
        partnerConfig,
        betType,
        events: selections,
      }),
    [config, betType, selections, partnerConfig]
  );

  const canUseProfitBoost = useCallback(() => {
    if (!stake) return true;

    if (!usageRules || !totalOdd) return false;
    if (profitBoostStake && +stake > profitBoostStake) return false;
    if (+stake < usageRules.minStake) return false;
    // Bet builder cards count as ONE selection for wheel-config min/max
    // bounds — same convention used by bet-type resolution and the
    // System combinations dropdown.
    const effectiveCount = getEffectiveSelectionsCount(selections);
    if (!inRange(effectiveCount, usageRules.minSelections, usageRules.maxSelections)) return false;
    if (!inRange(totalOdd, usageRules.minOdd, usageRules.maxOdd)) return false;
    return !expressBonus.amount;
  }, [usageRules, totalOdd, profitBoostStake, selections, stake]);

  useEffect(() => {
    if (betType === BetTypeEnum.Single && config.enableSingleStakeInput) {
      setMainState(oldMainState => ({ ...oldMainState, stake: "" }));
    }
  }, [betType, config, setMainState]);

  const { expressBonus } = useMemo(
    () =>
      getBetCalculations([BetCalculationsEnum.Bonus], {
        config,
        partnerConfig,
        stake: +stake,
        events: selections,
        bonusRule: bonusRules,
        currencyConfig
      }),
    [config, partnerConfig, stake, selections, bonusRules, currencyConfig]
  );

  const handleBack = async () => {
    const isConfirmed = await openDeferPopup<boolean>(LEAVE_EDITING_POPUP);
    if (isConfirmed) onClear();
  };

  const changeStake = (stake: decimalString) => setMainState({ ...mainState, stake });

  const handlePlaceBet = async () => {
    if (mainState.placeBetLoading || placeBetDisabled(config, selections, mainState, upperDataState, bbState))
      return;

    if (systemUnitStake < partnerConfig?.min_sub_bet_stakes[currencyConfig?.name]) {
      toastWrapper('error', `Unit stake must be greater than ${partnerConfig?.min_sub_bet_stakes[currencyConfig?.name]}`);
      return;
    }

    if ((isProfitBoost && profitBoostPercent) && mainState.triggeredBetId) {
      const ticketResult = await TerminalSwarmActions.checkBetTicket(mainState.triggeredBetId * 10);

      if (ticketResult.state_name === "CashOut") {
        storeSetProfitBoostPercent(null);
        storeSetIsProfitBoost(false);
        storeTriggerdBetId(null);
        emitProfitBoostInfo(null, null)
        toastWrapper('error', 'Profit Boost can\'t be used, because the previous bet has been cashouted.');
        return;
      }
    }

    const invalidCounterOdd = selections.some(invalidCounterOfferOdd);

    if (invalidCounterOdd) {
      setSelections(store => ({
        ...store,
        selections: selections.map(sel => ({ ...sel, hasError: invalidCounterOfferOdd(sel) }))
      }));

      return toastWrapper("error", "Offered odd should be greater than existing one");
    }

    setMainState(oldMainState => ({ ...oldMainState, placeBetLoading: true }));
    emitBetProcessing({ placeBetLoading: true });

    try {
      if (verificationResponse?.data) {
        const validationResult = await upperDataState.verificationResolver?.validate(
          verificationResponse.data,
          +stake
        );
        if (!validationResult?.success) {
          return (
            validationResult?.errorMessage &&
            toastWrapper("error", validationResult.errorMessage)
          );
        }
      }

      if (profitBoostPercent && !isProfitBoost && !expressBonus.amount && canUseProfitBoost()) {
        const profitUseConfirmed = await openDeferPopup<boolean>(BOOSTED_ODD_POPUP);
        if (profitUseConfirmed) return storeSetIsProfitBoost(!isProfitBoost);
      }

      const succeedSelectionIds = (await placeBet({
        selections,
        amount: +stake,
        exactLosing: !!expressBonus.exactLosing,
        profitBoostPercent,
        BTag: verificationResponse?.data?.identifier,
      })) as number[];

      if (!!succeedSelectionIds?.length && isFreeBetEnabled) setOpenPopup({ key: SUCCESS_POPUP });
      if (isProfitBoost) {
        emitProfitBoostInfo(null, null);
      }

      onClear(succeedSelectionIds, true);
    } finally {
      setMainState(oldMainState => ({ ...oldMainState, placeBetLoading: false }));
      emitBetProcessing({ placeBetLoading: false });
    }
  };

  const handleBookBet = async () => {
    storeSetIsProfitBoost(false);

    if (mainState.bookBetLoading) return;

    storeSetBookBetLoading(true);

    const data = await bookBet(selections, stake);
    const bookingId = data?.details?.Id;

    if (bookingId) {
      const { totalOdd, possibleWin } = getBetCalculations(
        [BetCalculationsEnum.TotalOdd, BetCalculationsEnum.PossibleWin],
        {
          config,
          partnerConfig,
          betType,
          stake: +stake,
          events: selections.map((item) => ({ ...item, alternateOdd: 0 })),
          systemCombinationCount,
        }
      );

      const bookingTicketData = {
        amount: +stake,
        typeId: betType,
        bookId: bookingId,
        price: OddConverter(config, totalOdd, 'decimal') || 0,
        systemMinCount: mainState.systemCombinationCount,
        selection_list: formatBookingTicketSelections(selections),
        posWin: possibleWin ? roundAmount(possibleWin, upperDataState.currencyConfig) : null,
      };

      onClear();
      emitBetBooked(bookingTicketData);
    } else toastWrapper("error", "Something went wrong while trying to book a bet");

    storeSetBookBetLoading(false);
  };

  const disableStakeInput = betType === BetTypeEnum.Single && config.enableSingleStakeInput;

  useEffect(() => {
    if (maxStake && config.enableAutomaticSuperbet) {
      const exceededMaxStakeLimit = +stake > maxStake;
      storeSetSuperbet(exceededMaxStakeLimit);
    }
  }, [config, maxStake, stake]);

  useEffect(() => {
    return () => setMainState(newMainState => ({ ...newMainState, placeBetLoading: false }));
  }, [setMainState]);

  const isSuperbetAvailable = useMemo(() => {
    if (
      config.superbetHiddenMode ||
      config.isViewMode ||
      (betType === BetTypeEnum.Single && selections.length > 1)
    )
      return false;

    if (selections.some(selection => selection.event.isRacingEvent)) return false;

    if (!partnerConfig?.is_superbet_on_live && selections.some(selection => selection.game.is_live))
      return false;

    if (!partnerConfig?.is_superbet_on_prematch && selections.some(selection => !selection.game.is_live))
      return false;

    return true;
  }, [betType, partnerConfig, selections, config.superbetHiddenMode, config.isViewMode]);

  const isRacingSelectionExists =
    racingTypesList.includes(mainState.betType) || selections.some(selection => selection.event.sp);

  const isEachWayAvailable = useMemo(
    () => selections.every(selection => selection.event.isRacingEvent && selection.event.ew_allowed),
    [selections]
  );

  useEffect(() => {
    if (!isEachWayAvailable) storeSetEachWay(false);
  }, [isEachWayAvailable]);

  const hidePlaceBet = !isPlaceBetAvailable(config, upperDataState);

  return (
    <div className="B-betslip-bet-block">
      {config.enableWinterMode && <img src={LeftSnow} alt="left snow" className="left-snow" />}
      <div className="B-betslip-bet-block-inner">
        {!config.isViewMode && <NotifyMessages />}

        {!config.isViewMode && <SuperbetInfo />}

        {betType === BetTypeEnum.System && (
          <SystemOptions isEachWayAvailable={isEachWayAvailable} />
        )}

        {!isFreeBetEnabled && !disableStakeInput && (
          <div
            className={`B-bet-stake-input-wrp ${isSuperbet ? "B-superbet-active" : ""
              }`}
          >
            {betType === BetTypeEnum.System ? (
              <SystemStakeInput totalStake={stake} onChange={changeStake} />
            ) : (
              <StakeInput
                value={stake}
                onChange={changeStake}
                showMaxBetButton
                className="B-stack"
              />
            )}

            {isSuperbetAvailable && <SuperbetToggle />}
            {isEachWayAvailable && betType !== BetTypeEnum.System && (
              <EachWayToggle />
            )}
          </div>
        )}

        {config.hasFreeBets && <FreeBetToggle />}

        {isFreeBetEnabled && <FreeBets />}

        {!config.verification.required && !upperDataState.verificationResponse?.success && (
          <VerificationToggle />
        )}

        {!!profitBoostPercent && <ProfitBoost canUseProfitBoost={canUseProfitBoost} />}
        {isRacingSelectionExists ? (
          <BetDetailsTotalStake stake={+stake} />
        ) : (
          <BetDetails stake={+stake} />
        )}
        {config.verification.enabled &&
          !upperDataState.verificationResponse?.data?.id &&
          !upperDataState.userId && !isBookingOnlyMode(config, upperDataState) && (
            <div className="B-verification-notification">
              <i className="global-icon icon-toast-error warning-icon" />
              {t("Document ID is required to place a bet.")}
            </div>
          )}
        {verificationResponse?.data && <VerificationAccount />}

        <div className="B-betslip-bet-btn-wrp">
          {!!mainState.editingBet ? (
            <button className="B-back-button" onClick={handleBack}>
              <i className="global-icon icon-slim-arrow-left" />
              {t("Back")}
            </button>
          ) : (
            <button
              className="B-clear-button global-icon icon-recycle"
              onClick={handleClear}
            />
          )}

          {!hidePlaceBet && <PlaceBetButton onClick={handlePlaceBet} />}
          {isBookingAvailable(config, upperDataState) && (
            <button
              disabled={isRacingSelectionExists}
              className={classnames(
                "B-bet-block-button B-book-bet-button",
                {
                  "place-bet-visible": !hidePlaceBet,
                }
              )}
              onClick={handleBookBet}
            >
              {t(config.bookBetButtonText)}
              {mainState.bookBetLoading && <Spinner />}
            </button>
          )}

          {config.enablePriceChangeTypeSelect && <PriceChangeTypeSelect />}
        </div>
      </div>
    </div>
  );
};

export default BetBlock;
